import { useEffect, useState, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import Lottie from 'lottie-react';
import axios from 'axios';
import { motion } from 'framer-motion';
import MobileFrame from '../components/MobileFrame';
import { Clock, Package } from "lucide-react";

// Import Lottie animations
import successAnimation from '../assets/sucess.json';
import failedAnimation from '../assets/rejected.json';
import approvedAnimation from '../assets/approved.json';
import errorAnimation from '../assets/error.json';

// TypeScript interfaces
interface StatusStyle {
  title: string;
  message: string;
  glowColor: string;
  borderColor: string;
  lottieFile: any;
  bgGradient: string;
  textColor: string;
  buttonColor: string;
  iconBg: string;
  iconColor: string;
}

interface StatusStylesMap {
  [key: number]: StatusStyle;
}

interface OrderDetails {
  orderId: string;
  callbackUrl?: string;
  partnerName?: string;
  customerMobileNumber?: string;
}

// Status Mapping with enhanced styling
const statusStyles: StatusStylesMap = {
  0: {
    title: "Transaction Failed",
    message: "Don't worry, we'll help you out. Please contact support@cleverpe.com for assistance.",
    borderColor: "#c52929",
    glowColor: "rgba(248, 113, 113, 0.3)",
    lottieFile: failedAnimation,
    bgGradient: "linear-gradient(to bottom, rgba(244, 139, 139, 0.1), rgba(93, 67, 67, 0.02))",
    textColor: "text-red-600",
    buttonColor: "bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700",
    iconBg: "bg-red-100",
    iconColor: "text-red-500"
  },
  1: {
    title: "Transaction Successful",
    message: "Your transaction has been successfully completed on CleverPe.",
    borderColor: "#13d15c",
    glowColor: "rgba(16, 185, 129, 0.3)",
    lottieFile: successAnimation,
    bgGradient: "linear-gradient(to bottom, rgba(72, 187, 120, 0.1), rgba(32, 39, 35, 0.02))",
    textColor: "text-green-600",
    buttonColor: "bg-gradient-to-r from-green-400 to-green-600 hover:from-green-500 hover:to-green-700",
    iconBg: "bg-green-100",
    iconColor: "text-green-500"
  },
  2: {
    title: "Transaction Approved",
    message: "Transaction exceeds your approved credit limit. Please create an order within your limit.",
    borderColor: "#efc57d",
    glowColor: "rgba(245, 158, 11, 0.3)",
    lottieFile: approvedAnimation,
    bgGradient: "linear-gradient(to bottom, rgba(250, 204, 21, 0.1), rgba(250, 204, 21, 0.02))",
    textColor: "text-amber-600",
    buttonColor: "bg-gradient-to-r from-yellow-200 to-yellow-400 hover:from-yellow-300 hover:to-yellow-500 text-gray-800 font-semibold",
    iconBg: "bg-amber-100",
    iconColor: "text-amber-500"
  },
  3: {
    title: "Error Processing Transaction",
    message: "We encountered an error while processing your transaction. Please try again or contact our support team.",
    borderColor: "#6b7280",
    glowColor: "rgba(107, 114, 128, 0.3)",
    lottieFile: errorAnimation,
    bgGradient: "linear-gradient(to bottom, rgba(156, 163, 175, 0.1), rgba(156, 163, 175, 0.02))",
    textColor: "text-gray-600",
    buttonColor: "bg-gradient-to-r from-gray-400 to-gray-600 hover:from-gray-500 hover:to-gray-700 text-white",
    iconBg: "bg-gray-100",
    iconColor: "text-gray-500"
  }
};

const LenderCallback = () => {
  const [searchParams] = useSearchParams();

  // Extract parameters from URL
  const orderId = searchParams.get('orderId');
  const transactionStatus = Number(searchParams.get('transactionStatus') || 3);
  const transactionId = searchParams.get('transactionId');



  // Default to error status if the transaction status is invalid
  // Use status mapping or default to error (3)
  const status = Object.prototype.hasOwnProperty.call(statusStyles, transactionStatus)
    ? transactionStatus
    : 3;

  // Destructure styling properties
  const {
    title,
    message,
    lottieFile,
    borderColor,
    bgGradient,
    textColor,
    buttonColor
  } = statusStyles[status];

  // State management
  const [loading, setLoading] = useState(true);
  const [apiError, setApiError] = useState<string | null>(null);
  const [isMobile, setIsMobile] = useState(false);
  const [orderDetails, setOrderDetails] = useState<OrderDetails | null>(null);
  const [redirectCountdown, setRedirectCountdown] = useState(10); // Changed from 5 to 10 seconds
  const [redirectTriggered, setRedirectTriggered] = useState(false);
  const [callbackUrlError, setCallbackUrlError] = useState<string | null>(null);
  const [showManualRedirect, setShowManualRedirect] = useState(false);

  useEffect(() => {
    // Check if the screen width is for mobile devices
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    // Initial check
    checkMobile();

    // Add event listener for window resize
    window.addEventListener('resize', checkMobile);

    // Cleanup
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Fetch order details including callback URL
  useEffect(() => {
    if (!orderId) {
      setLoading(false);
      setApiError("Missing order ID");
      return;
    }

    // Function to fetch order details
    const fetchOrderDetails = async () => {
      try {
        const response = await axios.get(
          `${import.meta.env.VITE_API_BASE_URL}/gateway/order/${orderId}`
        );

        if (response.data && response.data.success) {
          setOrderDetails(response.data.data);
        } else {
          throw new Error("Failed to fetch order details");
        }
      } catch (err: any) {
        // console.error("Error fetching order details:", err);
        setApiError(err.message || "Failed to fetch order details");
      }
    };

    if (orderId && transactionStatus !== undefined) {
      Promise.all([fetchOrderDetails()])
        .catch(err => {
          // console.error("Error in Promise.all:", err);
        })
        .finally(() => {
          setLoading(false); // <-- Ensure this runs in both success & error
        });
    } else {
      setLoading(false);
    }
  }, [orderId, transactionStatus, transactionId]);


  // Handle countdown and redirection
  const partnerNotifiedRef = useRef(false); // track if already notified

  useEffect(() => {
    if (loading || redirectTriggered) return;

    // Notify partner only once
    const notifyPartner = async () => {
      if (partnerNotifiedRef.current) return; // already notified
      partnerNotifiedRef.current = true;

      try {
        const partnerName = orderDetails?.partnerName?.toLowerCase() || "";

        if (partnerName.includes("vifin") || partnerName.includes("sample")) {
          await axios.post(
            `${import.meta.env.VITE_API_BASE_URL}/partner/loan-status`,
            {
              orderId,
              status,
              title,
              orderData: orderDetails,
            },
            { timeout: 5000 }
          );

        } else {
          // console.log("ℹ️ Partner does not require notification:", partnerName);
        }
      } catch (err: any) {
        // console.error("❌ Error notifying partner API:", err.message);
      }
    };

    notifyPartner();

    // Countdown logic for redirect
    if (redirectCountdown <= 0) {
      if (!redirectTriggered) setShowManualRedirect(true);
      setRedirectTriggered(true);

      // redirect only after countdown (partner already notified)
      window.location.href = orderDetails?.callbackUrl || import.meta.env.VITE_RETURN_URL || "https://cleverpe.com";
    } else {
      const timer = setTimeout(() => {
        setRedirectCountdown((prev) => prev - 1);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [
    redirectCountdown,
    loading,
    redirectTriggered,
    orderDetails,
    orderId,
    status,
    title,
  ]);






  // Function to handle manual redirection to callback URL or home
  const handleRedirectNow = () => {
    if (orderDetails?.callbackUrl) {
      window.location.href = orderDetails.callbackUrl;
    } else {
      window.location.href = import.meta.env.VITE_RETURN_URL || 'https://cleverpe.com';
    }
  };



  const date = new Date();

  const formattedDate = date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric"
  });

  const formattedTime = date.toLocaleTimeString("en-IN", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true
  });

  const contentComponent = (
    <div
      className="flex flex-col h-full pt-8 md:pt-10 pb-3 px-4"
      style={{
        background: bgGradient,
      }}
    >
      {/* Header */}
      <div className="mb-4 text-center">
        <motion.div
          initial={{ scale: 0.85, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.5 }}
          className="w-28 h-28 mx-auto mb-2"
        >
          <Lottie animationData={lottieFile} loop className="w-full h-full" />
        </motion.div>

        <h2 className={`text-2xl font-bold ${textColor} dark:text-gray-100`}>
          {title}
        </h2>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col items-center justify-start">

        {/* Info Card */}
        <div className="w-full max-w-xs rounded-2xl p-[1px] bg-gradient-to-r from-neutral-300/40 to-neutral-500/40 mt-4 mb-3 shadow-md">
          <div className="bg-gradient-to-b from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800 rounded-2xl px-4 py-3 relative overflow-hidden flex flex-col items-center text-center">

            {/* Top glow */}
            <div className="absolute top-0 left-0 w-full h-8 bg-gradient-to-b from-gray-400/20 to-transparent blur-sm pointer-events-none" />

            {/* Row 1 → Order ID */}
            <div className="flex flex-col items-center mb-2 w-full">
              <div className="flex items-center justify-center gap-2 mb-1">
                <div className="p-1.5 bg-blue-100 dark:bg-blue-900/30 rounded-full">
                  <Package
                    className="w-4 h-4"
                    style={{ color: borderColor }}
                  />
                </div>

                <span className="text-base font-semibold text-gray-900 dark:text-white break-all">
                  {orderId || "—"}
                </span>
              </div>

              {/* Thin underline */}
              <div
                className="w-20 h-[2px] rounded-full"
                style={{
                  background: `linear-gradient(to right, transparent, ${borderColor}, transparent)`
                }}
              />
            </div>

            {/* Row 2 → Time */}
            <div className="flex items-center justify-center gap-2 mt-2">
              <div className="p-1.5 bg-gray-100 dark:bg-gray-800 rounded-full">
                <Clock
                  className="w-4 h-4"
                  style={{ color: borderColor }}
                />
              </div>

              <span className="text-xs text-gray-600 dark:text-gray-400">
                {formattedDate} • {formattedTime}
              </span>
            </div>

          </div>
        </div>

        {/* Message */}
        <p className="max-w-xs text-center text-base text-white whitespace-pre-line mt-8">
          {apiError ? `Error: ${apiError}` : message}
        </p>

        {/* Countdown */}
        {!redirectTriggered && !callbackUrlError && (
          <div className="w-full max-w-xs text-center mt-3">
            <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
              Redirecting in {redirectCountdown} seconds...
            </p>
            <div className="w-full h-1.5 bg-gray-200 rounded-full overflow-hidden dark:bg-gray-700 mt-1">
              <div
                className="h-full rounded-full transition-[width] duration-1000 ease-out"
                style={{
                  width: `${Math.max(0, Math.min(100, (redirectCountdown / 10) * 100))}%`,
                  backgroundColor: borderColor,
                  boxShadow: `0 0 6px ${borderColor}`
                }}
              />
            </div>
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="mt-auto flex flex-col items-center gap-2 pt-2">

        <img
          src="/CleverPeLogos/PoweredBy.svg"
          alt="Powered By CleverPe"
          className="h-9 opacity-90"
          onError={(e) => {
            e.currentTarget.src = 'https://placehold.co/140x40?text=Powered+By+CleverPe';
          }}
        />

        <div className="flex gap-3 w-full max-w-sm">
          <button
            className={`flex-1 h-11 ${buttonColor} rounded-full text-base font-semibold text-white`}
            onClick={handleRedirectNow}
            disabled={redirectTriggered}
          >
            {redirectTriggered ? "Redirecting..." : "Redirect Now"}
          </button>

          {(status === 0 || status === 3 || callbackUrlError) && (
            <button
              className="flex-1 h-11 text-base font-semibold text-gray-700 bg-white border border-gray-300 rounded-full dark:text-gray-300 dark:bg-gray-800 dark:border-gray-600"
              onClick={() => window.location.href = 'mailto:support@cleverpe.com'}
            >
              Contact Support
            </button>
          )}
        </div>
      </div>
    </div>
  );

  return isMobile ? (
    <div className="h-[100dvh] w-full p-5 overflow-hidden bg-white dark:bg-dark">
      {contentComponent}
    </div>
  ) : (
    <MobileFrame>
      {contentComponent}
    </MobileFrame>
  );
};

export default LenderCallback; 